import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';

const DashboardSection = () => {
  const [activeTab, setActiveTab] = useState(0);

  const screenshots = [
    {
      id: 'summary',
      label: 'Summary & Analysis',
      description: 'Get a clear overview of any policy with risk assessments and key points highlighted.',
      image: 'https://customer-assets.emergentagent.com/job_protect-plainly/artifacts/8lswym3j_IMG_0602.jpeg',
    },
    {
      id: 'profile',
      label: 'Family Risk Profile',
      description: 'Customize your risk tolerance based on your family\'s unique needs and children\'s ages.',
      image: 'https://customer-assets.emergentagent.com/job_protect-plainly/artifacts/gfv7fecl_IMG_0603.jpeg',
    },
    {
      id: 'scoring',
      label: 'Legal Scoring',
      description: 'See detailed breakdowns of Data Privacy, Content Rights, and Liability scores.',
      image: 'https://customer-assets.emergentagent.com/job_protect-plainly/artifacts/xgcq6ea3_IMG_0604.jpeg',
    },
    {
      id: 'plain',
      label: 'Plain English',
      description: 'Read complex terms translated into simple, easy-to-understand language.',
      image: 'https://customer-assets.emergentagent.com/job_protect-plainly/artifacts/g0cjnywg_IMG_0606.jpeg',
    },
  ];

  return (
    <section className="py-24 md:py-32 bg-stone-50" id="how-it-works" data-testid="dashboard-section">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span className="text-[#0D9488] font-medium text-sm uppercase tracking-wider">How It Works</span>
          <h2 
            className="font-heading text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-[#292524] mt-4"
            data-testid="dashboard-heading"
          >
            Your Family's Digital Shield
          </h2>
          <p className="mt-6 text-lg text-[#78716C] max-w-2xl mx-auto">
            A simple, intuitive dashboard that puts you in control of your family's digital privacy.
          </p>
        </motion.div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap justify-center gap-2 mb-8" data-testid="dashboard-tabs">
          {screenshots.map((tab, index) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(index)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeTab === index
                  ? 'bg-[#0D9488] text-white shadow-md'
                  : 'bg-white text-[#78716C] hover:bg-stone-100'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Screenshot Display */}
        <motion.div
          className="relative"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="absolute -inset-4 bg-[#0D9488]/5 rounded-[2rem] blur-3xl"></div>
          <div 
            className="relative bg-white rounded-3xl shadow-2xl overflow-hidden border border-stone-100"
            data-testid="dashboard-mockup"
          >
            {/* Screenshot Header */}
            <div className="bg-[#FDFBF7] border-b border-stone-100 px-6 py-4">
              <h3 className="font-heading font-medium text-[#292524]">{screenshots[activeTab].label}</h3>
              <p className="text-sm text-[#78716C] mt-1">{screenshots[activeTab].description}</p>
            </div>

            {/* Screenshot Image */}
            <div className="p-4 md:p-6 bg-stone-50">
              <motion.img
                key={activeTab}
                src={screenshots[activeTab].image}
                alt={screenshots[activeTab].label}
                className="w-full max-w-3xl mx-auto rounded-2xl shadow-lg"
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                data-testid={`screenshot-${screenshots[activeTab].id}`}
              />
            </div>
          </div>
        </motion.div>

        {/* Feature Highlights */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          {screenshots.map((feature, index) => (
            <div 
              key={feature.id}
              className={`p-6 rounded-2xl cursor-pointer transition-all ${
                activeTab === index 
                  ? 'bg-[#0D9488]/10 border-2 border-[#0D9488]' 
                  : 'bg-white border border-stone-100 hover:border-stone-200'
              }`}
              onClick={() => setActiveTab(index)}
              data-testid={`feature-card-${index}`}
            >
              <h4 className={`font-medium mb-2 ${activeTab === index ? 'text-[#0D9488]' : 'text-[#292524]'}`}>
                {feature.label}
              </h4>
              <p className="text-sm text-[#78716C]">{feature.description}</p>
            </div>
          ))}
        </motion.div>

        {/* CTA Below Dashboard */}
        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Button
            size="lg"
            className="rounded-full bg-[#0D9488] hover:bg-[#0F766E] text-white px-8 py-6 text-lg font-medium shadow-lg shadow-teal-900/10 hover:shadow-teal-900/20 transition-all hover:-translate-y-0.5"
            data-testid="dashboard-cta"
            asChild
          >
            <a href="https://app.trysaidsimply.com/auth">Try It Free for 14 Days</a>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default DashboardSection;
